"use client";

import Image from "next/image";
import React, { useState, useRef, useEffect } from "react";
import clsx from "clsx";
import Link from "next/link";
import { toWebp } from "@/utils/common";

interface AccordionItem {
  id: string;
  tag: string;
  title: string;
  description: string;
  image: any;
  link?:
    | {
        label: string;
        url: string;
      }
    | undefined;
}

interface CustomAccordionProps {
  accordionItems: AccordionItem[];
}

const baseUrl = `${process.env.NEXT_PUBLIC_REACT_APP_RESOURCE_BASE_URL}`;

const CustomAccordion: React.FC<CustomAccordionProps> = ({
  accordionItems,
}) => {
  const [openId, setOpenId] = useState<string | null>(
    accordionItems.length > 0 ? accordionItems[0].id : null,
  );
  const contentRefs = useRef<(HTMLDivElement | null)[]>([]);

  useEffect(() => {
    accordionItems.forEach((item, idx) => {
      const ref = contentRefs.current[idx];
      if (ref) {
        ref.style.maxHeight =
          openId === item.id ? `${ref.scrollHeight}px` : "0px";
      }
    });
  }, [openId, accordionItems]);

  const toggleAccordion = (id: string) => {
    setOpenId((prev) => (prev === id ? null : id));
  };

  const trimToTwoLines = (text: string = "", maxWords = 5) => {
    const words = text.trim().split(" ");
    return words.length > maxWords
      ? words.slice(0, maxWords).join(" ") + "..."
      : text;
  };

  return (
    <>
      {accordionItems.map((item, idx) => {
        const isOpen = openId === item.id;
        const isBlue = idx % 2 === 0;

        return (
          <div key={item.id} className=" overflow-hidden h-auto border-0 mb-4">
            {/* Hide header when accordion is open */}
            {!isOpen && (
              <button
                onClick={() => toggleAccordion(item.id)}
                className={clsx(
                  "w-full text-left h-[115px] relative font-semibold flex justify-between items-center",
                  isBlue
                    ? "bg-blue-100 text-blue-800"
                    : "bg-green-100 text-green-800",
                )}
              >
                <div className="cursor-pointer w-full">
                  <div
                    className={clsx(
                      "relative h-[115px] xl:px-3 px-6 xl:py-[35px] py-6 xl:pt-[18px] sm:pt-[35px] pt-[12px] flex flex-col justify-end items-start",
                      isBlue ? "bg-blue-50" : "bg-green-50",
                      isBlue
                        ? "after:bg-[url(/images/slider/blue-slide.png)] before:bg-[url(/images/slider/blue-shard-mobile.svg)] before:will-change-transform lg:before:bg-[url(/images/slider/blue-shard.webp)]"
                        : "after:bg-[url(/images/slider/green-slide.png)] before:bg-[url(/images/slider/green-shard-mobile.svg)] before:will-change-transform lg:before:bg-[url(/images/slider/green-shard.webp)]",
                      "after:content-[''] after:absolute after:w-[44px] lg:after:w-[58px] after:h-[85px] lg:after:h-[115px] after:top-0 after:right-0 after:bg-contain after:bg-no-repeat after:bg-right-bottom",
                      "before:content-[''] before:absolute before:w-full before:h-full before:top-0 before:left-0 before:bg-cover before:bg-no-repeat before:bg-center",
                    )}
                  >
                    <span className="absolute top-1.5 left-1.5 w-2.5 h-2.5 bg-white rounded-full shadow z-10" />
                    <span className="absolute top-1.5 right-1.5 w-2.5 h-2.5 bg-white rounded-full shadow z-10" />
                    <span className="absolute bottom-1.5 left-1.5 w-2.5 h-2.5 bg-white rounded-full shadow z-10" />
                    <span className="absolute bottom-1.5 right-1.5 w-2.5 h-2.5 bg-white rounded-full shadow z-10" />
                    <p className="text-heading-6 font-bold text-primary-text uppercase">
                      {item?.tag}
                    </p>

                    <p className="text-subtitle-3 text-secondary-text opacity-70 mt-3 block line-clamp-2" title={item?.title}>
                      {trimToTwoLines(item?.title,2)}
                    </p>
                  </div>
                </div>
              </button>
            )}

            {/* Accordion Content */}
            <div
              ref={(el) => {
                contentRefs.current[idx] = el;
              }}
              className="transition-all duration-500 overflow-hidden"
              style={{ maxHeight: 0 }}
            >
              <div className="relative w-full min-h-[500px] group">
                <div
                  className="absolute top-0 right-0 w-full h-full z-10"
                  style={{
                    background:
                      "linear-gradient(180deg, rgba(0,0,0,0.08) 0%, rgba(0,0,0,0.8) 60.02%)",
                  }}
                ></div>

                <Image
                  alt={item?.tag}
                  src={toWebp(`${baseUrl}${item?.image?.data?.attributes.url}`)}
                  fill
                  className={clsx(
                    "object-cover transition-transform duration-700 ease-in-out",
                    isOpen ? "scale-105" : "scale-100",
                  )}
                />

                <div
                  className={clsx(
                    "absolute z-20 xl:bottom-10 lg:bottom-8 bottom-0 xl:left-10 lg:left-8 left-0 xl:max-w-[540px] max-w-[557px] lg:w-[50%] lg:p-0 p-6 w-full transition-all duration-700",
                    isOpen
                      ? "opacity-100 translate-y-0"
                      : "opacity-0 translate-y-4",
                  )}
                >
                  <p className="text-subtitle-3 text-white border border-white w-fit p-2.5 pb-1.5">
                    {item?.tag}
                  </p>
                  <h2 className="text-heading-3 font-bold text-white mt-5">
                    {item?.title}
                  </h2>
                  <p className="text-subtitle-2 text-white mt-2 line-clamp-8">
                    {trimToTwoLines(item?.description)}
                  </p>
                  <div className="mt-4">
                    {item?.link?.url && item?.link?.label && (
                      <Link href={item?.link?.url}>
                        <button
                          aria-label="Explore Services"
                          type="button"
                          className="flex items-center justify-center gap-2.5 text-subtitle-2 font-semibold transition-all duration-300 group
                          bg-text-button hover:bg-blue-700 cursor-pointer text-primary-text-white
                          undefined px-5 xl:pt-[17px] xl:pb-[13px] pt-[14.5px] pb-[10.5px]"
                        >
                          {item?.link?.label}
                          <svg
                            width="20"
                            height="20"
                            viewBox="0 0 20 20"
                            fill="none"
                            xmlns="http://www.w3.org/2000/svg"
                            className="my-[3px]"
                          >
                            <path
                              d="M1.25 9.6582H17.75"
                              stroke="white"
                              strokeWidth="1.5"
                              strokeLinecap="square"
                              strokeLinejoin="round"
                            />
                            <path
                              d="M11.5908 2.5L18.7499 9.65909L11.5908 16.8181"
                              stroke="white"
                              strokeWidth="1.5"
                              strokeLinecap="square"
                              strokeLinejoin="round"
                            />
                          </svg>
                        </button>
                      </Link>
                    )}
                  </div>
                </div>
              </div>
            </div>
          </div>
        );
      })}
    </>
  );
};

export default CustomAccordion;
